import React, {
  type ReactElement,
  type ReactNode,
  createContext,
  useContext,
  useMemo
} from 'react';
import type { AuthorisableUser } from 'policies/types';
import { useCustomerAreas } from 'services/assist-api/entities/customers-areas/customers-areas';
import type { CustomerAreaWithMarkingEnabled } from 'services/assist-api/entities/customers-areas/types';
import type { ROLE } from 'services/assist-api/entities/role/roles';
import { useAuth } from 'services/providers/auth.provider';
import { isInitialLoad } from 'utils/check-query-status';

const PageAccessContext = createContext({
  authorisableUser: {
    roles: [] as ROLE[]
  },
  isLoading: true // Set to false once all dependencies of authorisableUser have loaded.
});

type Props = {
  children: ReactNode;
};

export default function PageAccessProvider({ children }: Props): ReactElement {
  const { me, isLoading: isAuthLoading } = useAuth();

  // Fetch all areas that the user is subscribed that have marking enabled. This could be expanded
  // to authorise other pages based on current subscriptions or other rules.
  // Note: the customer areas should always be fetched and authorised, regardless of whether the
  // user is currently logged into a customer.
  const customerAreasQuery = useCustomerAreas<CustomerAreaWithMarkingEnabled>(
    {
      filters: {
        customer: me?.customer?.id,
        'area.markingEnabled': true
      },
      groups: ['area-markingenabled'],
      include: 'area'
    },
    { enabled: !isAuthLoading }
  );
  const customerAreas = customerAreasQuery?.data?.json || [];

  /**
   * All of the users roles for current context, a combination of:
   *
   * their system level roles (e.g. ROLE_SITE_ADMIN)
   * their customer level roles (e.g. ROLE_ADMIN)
   */
  function combineRoleLists(systemRoles: ROLE[], customerRoles: ROLE[]): ROLE[] {
    return customerRoles.concat(systemRoles);
  }

  const systemAndCustomerRoles: ROLE[] = useMemo(
    () => combineRoleLists((me?.systemRoles as ROLE[]) || [], (me?.customerRoles as ROLE[]) || []),
    [me?.systemRoles, me?.customerRoles]
  );

  const isLoading = isAuthLoading || isInitialLoad(customerAreasQuery);

  // Authorisation focused user that can be passed to authorisation policies constructed in the
  // context of the marking section, but could be expanded to authorise other pages:
  const authorisableUser: AuthorisableUser = useMemo(
    () => ({
      id: me?.id, // Can be undefined while loading, but should be set after.
      customerUser: me?.customerUser, // Acceptable to be undefined when not in organisation.
      roles: systemAndCustomerRoles, // Should always be set, otherwise authorisation may fail.
      belongsToMarkingOrganisation: customerAreas.length > 0 // Used for accessing Marking pages.
    }),
    [me, systemAndCustomerRoles, customerAreas.length]
  );

  const value = useMemo(() => ({ authorisableUser, isLoading }), [authorisableUser, isLoading]);

  return <PageAccessContext.Provider value={value}>{children}</PageAccessContext.Provider>;
}

export const usePageAccess = (): {
  authorisableUser: AuthorisableUser;
  isLoading: boolean;
} => useContext(PageAccessContext);
